import {
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EnvironmentOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  QuestionOutlined
} from "@ant-design/icons"
import { App, Button, DatePicker, Modal, Select, Switch, Tag } from "antd"
import dayjs from "dayjs"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { attendanceLabelKey } from "../helpers/calendar.ts"
import { downloadAppointmentIcs } from "../helpers/ics.ts"
import {
  acceptProposedTimeThunk,
  cancelCalendarEventThunk,
  requestCalendarRescheduleThunk,
  rsvpToCalendarEventThunk,
  updateReminderPreferenceThunk
} from "../redux/portal.thunks.ts"
import type {
  PortalCalendarEvent,
  PortalCalendarItem,
  PortalRsvpResponse
} from "../types/portal.types.ts"
import { CalendarEventFormModal } from "./CalendarEventFormModal.tsx"

const RSVP_TOAST_KEYS: Record<PortalRsvpResponse, string> = {
  accepted: "portal.calendar.rsvpAcceptedToast",
  tentative: "portal.calendar.rsvpTentativeToast",
  declined: "portal.calendar.rsvpDeclinedToast"
}

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  LEAD_MEETING: "leadMeeting",
  SITE_VISIT: "siteVisit",
  INSPECTION: "inspection",
  PROJECT_MEETING: "projectMeeting",
  FOLLOW_UP: "followUp",
  PERSONAL: "personal"
}

const REMINDER_OFFSET_OPTIONS = [5, 10, 15, 30, 60, 1440]

const RSVP_TAG_COLOR: Record<string, string> = {
  Accepted: "green",
  Declined: "red",
  Pending: "default"
}

interface CalendarEventDetailModalProps {
  open: boolean
  onClose: () => void
  item: PortalCalendarItem | null
}

export function CalendarEventDetailModal({
  open,
  onClose,
  item: passedItem
}: CalendarEventDetailModalProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { message, modal } = App.useApp()
  const liveEvents = useAppSelector((state) => state.portal.calendarEvents)

  // `passedItem` is the snapshot the caller had when the modal opened. For a
  // real appointment, an RSVP/reschedule/edit updates the store's copy in
  // place (see portal.slice.ts) — read that live copy instead, or the panel
  // would keep showing what it was before the action was even taken. A
  // milestone is never mutated here, so the passed snapshot is already live.
  let item = passedItem
  if (passedItem?.kind === "appointment") {
    const live = liveEvents.find((event) => event.id === passedItem.id)
    if (live) item = { ...live, kind: "appointment" }
  }
  const [rsvpSubmitting, setRsvpSubmitting] = useState<PortalRsvpResponse | null>(null)
  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  const [rescheduleSuggestedAt, setRescheduleSuggestedAt] = useState<dayjs.Dayjs | null>(null)
  const [rescheduleNote, setRescheduleNote] = useState("")
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false)
  const [acceptingProposedTime, setAcceptingProposedTime] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [reminderSaving, setReminderSaving] = useState(false)

  const resetRescheduleForm = () => {
    setRescheduleOpen(false)
    setRescheduleSuggestedAt(null)
    setRescheduleNote("")
  }

  const handleClose = () => {
    resetRescheduleForm()
    onClose()
  }

  if (!item) return null

  const isAppointment = item.kind === "appointment"
  const appointment = item.kind === "appointment" ? item : null
  const milestone = item.kind === "milestone" ? item : null

  const handleRsvp = async (response: PortalRsvpResponse) => {
    if (!appointment) return
    setRsvpSubmitting(response)
    const result = await dispatch(
      rsvpToCalendarEventThunk({ eventId: appointment.id, response })
    )
    setRsvpSubmitting(null)
    if (rsvpToCalendarEventThunk.fulfilled.match(result)) {
      message.success(t(RSVP_TOAST_KEYS[response]))
    } else {
      message.error(t("portal.calendar.rsvpFailedToast"))
    }
  }

  const handleRescheduleSubmit = async () => {
    if (!appointment) return
    setRescheduleSubmitting(true)
    const result = await dispatch(
      requestCalendarRescheduleThunk({
        eventId: appointment.id,
        suggestedStartAt: rescheduleSuggestedAt?.toISOString(),
        note: rescheduleNote.trim() || undefined
      })
    )
    setRescheduleSubmitting(false)
    if (requestCalendarRescheduleThunk.fulfilled.match(result)) {
      message.success(t("portal.calendar.rescheduleRequestedToast"))
      resetRescheduleForm()
    } else {
      message.error(t("portal.calendar.rescheduleFailedToast"))
    }
  }

  const handleAcceptProposedTime = async () => {
    if (!appointment) return
    setAcceptingProposedTime(true)
    const result = await dispatch(acceptProposedTimeThunk(appointment.id))
    setAcceptingProposedTime(false)
    if (acceptProposedTimeThunk.fulfilled.match(result)) {
      message.success(t("portal.calendar.acceptTimeAcceptedToast"))
    } else {
      message.error(t("portal.calendar.acceptTimeFailedToast"))
    }
  }

  // "Remind me" is personal and saves immediately on change — there is no
  // separate submit step, since this never touches anyone else's row
  // (`reminder-preference` always writes the caller's own).
  const handleReminderChange = async (offsetMinutes: number | null, remindAtStart: boolean) => {
    if (!appointment) return
    setReminderSaving(true)
    const result = await dispatch(
      updateReminderPreferenceThunk({
        eventId: appointment.id,
        payload: { reminder_offset_minutes: offsetMinutes, remind_at_start: remindAtStart }
      })
    )
    setReminderSaving(false)
    if (!updateReminderPreferenceThunk.fulfilled.match(result)) {
      message.error(t("portal.calendar.reminderUpdateFailedToast"))
    }
  }

  const handleCancelEvent = () => {
    if (!appointment) return
    modal.confirm({
      title: t("portal.calendar.cancelEventConfirmTitle"),
      content: t("portal.calendar.cancelEventConfirmContent"),
      okText: t("portal.calendar.cancelEventConfirmOk"),
      cancelText: t("portal.calendar.cancelEventConfirmCancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        setCancelling(true)
        const result = await dispatch(cancelCalendarEventThunk(appointment.id))
        setCancelling(false)
        if (cancelCalendarEventThunk.fulfilled.match(result)) {
          message.success(t("portal.calendar.cancelEventSuccessToast"))
        } else {
          message.error(t("portal.calendar.cancelEventFailedToast"))
        }
      }
    })
  }

  const categoryLabel = appointment?.category
    ? t(`portal.calendar.category.${CATEGORY_LABEL_KEYS[appointment.category] ?? "other"}`)
    : null

  const attendance = appointment ? attendanceLabelKey(appointment) : "info"
  const isCancelled = appointment?.status === "CANCELLED"
  const isPrivate = appointment?.event_kind === "private_reminder"
  const isOwnAppointment = appointment?.event_kind === "contractor_appointment"
  const canRespond =
    appointment && !isCancelled && appointment.rsvp_status !== null && attendance !== "info"
  const canEdit = !!appointment?.can_edit && !isCancelled
  const pendingRequest = appointment?.pending_reschedule_request ?? null
  const messageHref = !isPrivate && appointment?.organizer?.email
    ? `mailto:${appointment.organizer.email}?subject=${encodeURIComponent(
        `${t("portal.calendar.messageSubjectPrefix")}: ${item.title}${
          appointment.project_name ? ` (${appointment.project_name})` : ""
        }`
      )}`
    : null

  return (
    <Modal open={open} onCancel={handleClose} footer={null} title={item.title} width={520}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          {item.project_name && <Tag color="blue">{item.project_name}</Tag>}
          {categoryLabel && <Tag>{categoryLabel}</Tag>}
          {isCancelled && <Tag color="red">{t("portal.calendar.cancelled")}</Tag>}
          {isPrivate && (
            <Tag icon={<LockOutlined />}>{t("portal.calendar.onlyVisibleToYou")}</Tag>
          )}
          {item.kind === "milestone" && (
            <Tag color="default">{t("portal.calendar.milestoneTag")}</Tag>
          )}
        </div>

        <div className="flex items-start gap-2 text-sm text-gray-700">
          <CalendarOutlined className="mt-0.5 text-gray-400" />
          {isAppointment && appointment ? (
            <div>
              <p>
                {dayjs(appointment.start_at).format("dddd, MMMM D, YYYY")}
                {" · "}
                {appointment.all_day
                  ? t("portal.calendar.allDay")
                  : `${dayjs(appointment.start_at).format("h:mm A")} – ${dayjs(appointment.end_at).format("h:mm A")}`}
              </p>
              <p className="text-xs text-gray-400">{appointment.timezone}</p>
            </div>
          ) : milestone ? (
            <p>
              {milestone.start_date && milestone.end_date && milestone.start_date !== milestone.end_date
                ? `${dayjs(milestone.start_date).format("MMM D")} – ${dayjs(milestone.end_date).format("MMM D, YYYY")}`
                : dayjs(milestone.end_date || milestone.start_date || undefined).format("MMMM D, YYYY")}
              {" · "}
              <span className="text-gray-400">{t("portal.calendar.estimatedDate")}</span>
            </p>
          ) : null}
        </div>

        {isAppointment && appointment?.location && (
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <EnvironmentOutlined className="mt-0.5 text-gray-400" />
            {appointment.location_map_url ? (
              <a
                href={appointment.location_map_url}
                target="_blank"
                rel="noreferrer"
                className="hover:text-primary"
              >
                {appointment.location}
              </a>
            ) : (
              <span>{appointment.location}</span>
            )}
          </div>
        )}

        {isAppointment && appointment?.meeting_link && (
          <div className="flex items-center gap-2 text-sm">
            <LinkOutlined className="text-gray-400" />
            <a href={appointment.meeting_link} target="_blank" rel="noreferrer" className="text-primary">
              {t("portal.calendar.joinMeeting")}
            </a>
          </div>
        )}

        {isAppointment && appointment?.description && (
          <p className="whitespace-pre-line text-sm text-gray-600">{appointment.description}</p>
        )}

        {isAppointment && !isPrivate && appointment?.organizer && (
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <MailOutlined className="mt-0.5 text-gray-400" />
            <div>
              <p>{appointment.organizer.name}</p>
              {appointment.organizer.email && (
                <a href={`mailto:${appointment.organizer.email}`} className="text-xs text-primary">
                  {appointment.organizer.email}
                </a>
              )}
            </div>
          </div>
        )}

        {(isOwnAppointment || isPrivate) && appointment && appointment.attendees.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-md border border-gray-100 p-3">
            <p className="text-xs font-medium uppercase text-gray-400">
              {t("portal.calendar.teamAndGuests")}
            </p>
            {appointment.attendees.map((attendee) => (
              <div
                key={`${attendee.kind}-${attendee.email}`}
                className="flex items-center justify-between gap-2 text-sm"
              >
                <span className="truncate text-gray-800">{attendee.name ?? attendee.email}</span>
                <Tag color={RSVP_TAG_COLOR[attendee.status]}>
                  {t(`portal.calendar.rsvp.${attendee.status.toLowerCase()}`)}
                </Tag>
              </div>
            ))}
          </div>
        )}

        {isAppointment && appointment?.reminder && !isCancelled && (
          <div className="flex flex-col gap-2 rounded-md border border-gray-100 p-3">
            <p className="text-xs font-medium uppercase text-gray-400">
              {t("portal.calendar.remindersLabel")}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-700">{t("portal.calendar.create.beforeMeeting")}</span>
              <Select
                className="w-40"
                value={appointment.reminder.offset_minutes}
                disabled={reminderSaving}
                onChange={(value) => void handleReminderChange(value, appointment.reminder!.remind_at_start)}
                options={[
                  { value: null, label: t("portal.calendar.create.reminderNone") },
                  ...REMINDER_OFFSET_OPTIONS.map((minutes) => ({
                    value: minutes,
                    label:
                      minutes < 60
                        ? t("portal.calendar.create.reminderMinutes", { count: minutes })
                        : minutes === 60
                          ? t("portal.calendar.create.reminderHour")
                          : t("portal.calendar.create.reminderDay")
                  }))
                ]}
              />
            </div>
            <label className="flex items-center gap-2">
              <Switch
                checked={appointment.reminder.remind_at_start}
                disabled={reminderSaving}
                onChange={(checked) => void handleReminderChange(appointment.reminder!.offset_minutes, checked)}
              />
              <span className="text-sm text-gray-700">{t("portal.calendar.create.remindAtStart")}</span>
            </label>
            <p className="text-xs text-gray-400">{t("portal.calendar.create.reminderPersonalNote")}</p>
          </div>
        )}

        {attendance !== "info" && !isCancelled && (
          <div
            className={
              attendance === "required"
                ? "rounded-md bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700"
                : "rounded-md bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700"
            }
          >
            {attendance === "required"
              ? t("portal.calendar.attendanceRequired")
              : t("portal.calendar.attendanceOptional")}
          </div>
        )}
        {item.kind === "milestone" && (
          <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
            {t("portal.calendar.attendanceInfo")}
          </div>
        )}

        {appointment?.rsvp_status && !isCancelled && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-100 px-3 py-2">
            <span className="text-sm text-gray-500">
              {t("portal.calendar.yourResponse")}:{" "}
              <span className="font-medium text-gray-800">
                {t(`portal.calendar.rsvp.${appointment.rsvp_status.toLowerCase()}`)}
              </span>
            </span>
            {canRespond && (
              <div className="flex gap-2">
                <Button
                  type={appointment.rsvp_status === "Accepted" ? "primary" : "default"}
                  icon={<CheckOutlined />}
                  loading={rsvpSubmitting === "accepted"}
                  disabled={!!rsvpSubmitting}
                  onClick={() => void handleRsvp("accepted")}
                >
                  {t("portal.calendar.accept")}
                </Button>
                <Button
                  type={appointment.rsvp_status === "Tentative" ? "primary" : "default"}
                  icon={<QuestionOutlined />}
                  loading={rsvpSubmitting === "tentative"}
                  disabled={!!rsvpSubmitting}
                  onClick={() => void handleRsvp("tentative")}
                >
                  {t("portal.calendar.maybe")}
                </Button>
                <Button
                  danger={appointment.rsvp_status === "Declined"}
                  icon={<CloseOutlined />}
                  loading={rsvpSubmitting === "declined"}
                  disabled={!!rsvpSubmitting}
                  onClick={() => void handleRsvp("declined")}
                >
                  {t("portal.calendar.decline")}
                </Button>
              </div>
            )}
          </div>
        )}

        {isOwnAppointment && appointment?.attendee_response_status && !isCancelled && (
          <div className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
            <span className="text-sm text-gray-500">
              {appointment.attendee_response_status === "Pending" ? (
                t("portal.calendar.awaitingContractorResponse")
              ) : (
                <>
                  {t("portal.calendar.yourResponse") /* reused label as a generic "status" row here */}:{" "}
                  <span className="font-medium text-gray-800">
                    {t(`portal.calendar.rsvp.${appointment.attendee_response_status.toLowerCase()}`)}
                  </span>
                </>
              )}
            </span>
          </div>
        )}

        {pendingRequest &&
          !isCancelled &&
          (pendingRequest.initiated_by_organizer ? (
            <div className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-700">
              <div className="flex items-center gap-1.5 font-medium">
                <ClockCircleOutlined />
                {t("portal.calendar.newTimeProposed")}
              </div>
              {pendingRequest.suggested_start_at && (
                <p className="mt-1">
                  {dayjs(pendingRequest.suggested_start_at).format("dddd, MMMM D · h:mm A")}
                </p>
              )}
              {pendingRequest.note && <p className="mt-1 text-blue-600">{pendingRequest.note}</p>}
              <Button
                type="primary"
                className="mt-2"
                loading={acceptingProposedTime}
                onClick={() => void handleAcceptProposedTime()}
              >
                {t("portal.calendar.acceptNewTime")}
              </Button>
            </div>
          ) : (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <ClockCircleOutlined className="mr-1.5" />
              {t("portal.calendar.rescheduleRequested")}
            </div>
          ))}

        {item.kind === "appointment" &&
          item.event_kind === "gc_invitation" &&
          !isCancelled &&
          !pendingRequest && (
            <div>
              {!rescheduleOpen ? (
                <Button block onClick={() => setRescheduleOpen(true)}>
                  {t("portal.calendar.requestAnotherTime")}
                </Button>
              ) : (
                <div className="flex flex-col gap-2 rounded-md border border-gray-100 p-3">
                  <label className="text-xs font-medium uppercase text-gray-400">
                    {t("portal.calendar.suggestedTime")}
                  </label>
                  <DatePicker
                    showTime
                    className="w-full"
                    value={rescheduleSuggestedAt}
                    onChange={setRescheduleSuggestedAt}
                  />
                  <TextArea
                    rows={2}
                    placeholder={t("portal.calendar.rescheduleNotePlaceholder")}
                    value={rescheduleNote}
                    onChange={(e) => setRescheduleNote(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button onClick={resetRescheduleForm} disabled={rescheduleSubmitting}>
                      {t("portal.upload.cancel")}
                    </Button>
                    <Button
                      type="primary"
                      loading={rescheduleSubmitting}
                      onClick={() => void handleRescheduleSubmit()}
                    >
                      {t("portal.calendar.submitRequest")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
          {canEdit && (
            <Button onClick={() => setEditOpen(true)}>{t("portal.calendar.edit")}</Button>
          )}
          {canEdit && (
            <Button danger loading={cancelling} onClick={handleCancelEvent}>
              {t("portal.calendar.cancelEvent")}
            </Button>
          )}
          {messageHref && (
            <Button icon={<MailOutlined />} href={messageHref}>
              {t("portal.calendar.messageAboutThis")}
            </Button>
          )}
          {isAppointment && appointment && (
            <Button onClick={() => downloadAppointmentIcs(appointment)}>
              {t("portal.calendar.addToCalendar")}
            </Button>
          )}
        </div>
      </div>

      {appointment && (
        <CalendarEventFormModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          editingEvent={appointment as PortalCalendarEvent}
        />
      )}
    </Modal>
  )
}
